import React from 'react';
import { useSelector } from 'react-redux';

import { AreaChart } from 'components';
import { REAL_TIME_SCENARIO, selectors } from 'state';
import {
  formatters,
  buildPredeterminedReturnSeries,
  isPredeterminedSession,
} from 'services';

type Props = {
  height?: number | string;
  style?: React.CSSProperties;
};

const PortfolioValueEvolution: React.FC<Props> = ({ height, style }) => {
  const player = useSelector(selectors.player);
  const { player_history, metrics } = player;
  const session = useSelector(selectors.activeSession);

  const isRealTime = session.scenario_type === REAL_TIME_SCENARIO;
  const isPredetermined = isPredeterminedSession(session);

  let evolutionData = player_history.map((item, index) => ({
    x: isRealTime ? item.timestamp : index * session.frequency + 1,
    y: item.portfolio_return,
  }));

  if (session.show_graph_as_rt) {
    evolutionData = player_history.map((item, index) => ({
      x: item.timestamp,
      y: item.portfolio_return,
    }));
  }
  let benchmarkData = metrics.map((item, index) => ({
    x: isRealTime ? item.timestamp : index * session.frequency + 1,
    y: item.market_index_return,
  }));
  if (session.show_graph_as_rt) {
    benchmarkData = metrics.map((item, index) => ({
      x: item.timestamp,
      y: item.market_index_return,
    }));
  }

  if (isPredetermined) {
    // THIRD branch (fixed `index*frequency+1` and RT `timestamp` above stay byte-for-byte):
    // knit canonical anchors + display fills onto a numeric days-since-start axis, so the
    // uneven gaps space correctly without leaking the absolute calendar to trainees.
    const knitted = buildPredeterminedReturnSeries(player, session, 'trainee');
    evolutionData = knitted.portfolio;
    benchmarkData = knitted.benchmark;
  }

  if (isRealTime) {
    // Delete all the first points with return 0
    while (evolutionData.length && evolutionData[0].y === 0) {
      evolutionData.splice(0, 1);
    }
    while (benchmarkData.length && benchmarkData[0].y === 0) {
      benchmarkData.splice(0, 1);
    }
  }
  // if (session.benchmark_constant !== 0) {
  //   benchmarkData[0].y = session.benchmark_constant;
  // }
  const sessionHasNoBenchmark =
    session.benchmark_portfolio_compositions.length === 0 &&
    session.benchmark_compositions.length === 0 &&
    session.benchmark_constant === 0 &&
    !session.allow_individual_benchmark;

  const xaxis = isRealTime
    ? undefined
    : {
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: function (value: unknown) {
            return `Day ${value}`;
          },
          show: false,
        },
      };
  // console.log('evolutionData', evolutionData);

  const chartStyle: React.CSSProperties = {
    width: '100%',
    minWidth: '350px',
    ...style,
  };

  if (sessionHasNoBenchmark) {
    return (
      <AreaChart
        title="Portfolio Return Evolution"
        dataSeriesName="Return"
        data={evolutionData}
        yaxisFormatter={formatters.percent1Digit}
        xaxis={xaxis}
        style={chartStyle}
        tooltipDateFormat={'d MMM HH:mm'}
        height={height}
      />
    );
  } else {
    return (
      <AreaChart
        title="Portfolio Return Evolution"
        dataSeriesName="Return"
        data={evolutionData}
        yaxisFormatter={formatters.percent1Digit}
        xaxis={xaxis}
        style={chartStyle}
        tooltipDateFormat={'d MMM HH:mm'}
        additionalLines={[{ name: 'Benchmark', data: benchmarkData }]}
        height={height}
      />
    );
  }
};

export default React.memo(PortfolioValueEvolution);
